import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../Typography';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TeamInfo {
  name: string;
  /** Logo slot — pass CountryFlag, TeamLogo, or any ReactNode. */
  logo: ReactNode;
  /** Goals scored. Absent = no result yet. */
  score?: number;
}

export interface MatchOverviewProps {
  leftTeam: TeamInfo;
  rightTeam: TeamInfo;
  /**
   * Kick-off time shown when neither team has a score — e.g. "12:30pm".
   * Ignored once scores are present.
   */
  kickOff?: string;
  /**
   * Match status label shown below the score — "FT", "HT", "32'", etc.
   * Only rendered when both teams have a score.
   */
  status?: string;
  /**
   * Drives the colour of the status badge.
   * - `finished` → dark navy  (#201d4a)
   * - `live`     → green      (#8ce28b)
   */
  statusVariant?: 'finished' | 'live';
  /** Optional venue / date line — "Mar 7th  |  Etihad Stadium" */
  venue?: string;
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * MatchOverview — team names, logos, and the centre score/time badge.
 *
 * Pass any logo component into the `logo` slot — CountryFlag for tournaments,
 * TeamLogo for league fixtures, etc. The component is completely agnostic.
 *
 * @example — upcoming league fixture
 * <MatchOverview
 *   leftTeam={{ name: 'Man City',    logo: <TeamLogo id={11} /> }}
 *   rightTeam={{ name: 'Bournemouth', logo: <TeamLogo id={91} /> }}
 *   kickOff="12:30pm"
 *   venue="Mar 7th  |  Etihad Stadium"
 * />
 *
 * @example — finished World Cup match
 * <MatchOverview
 *   leftTeam={{ name: 'USA',      logo: <CountryFlag code="US" />, score: 4 }}
 *   rightTeam={{ name: 'Paraguay', logo: <CountryFlag code="PY" />, score: 2 }}
 *   status="FT"
 *   statusVariant="finished"
 * />
 */
export const MatchOverview: FC<MatchOverviewProps> = ({
  leftTeam,
  rightTeam,
  kickOff,
  status,
  statusVariant = 'finished',
  venue,
  className,
}) => {
  const hasScore = leftTeam.score !== undefined && rightTeam.score !== undefined;

  return (
    <div
      className={twMerge(
        'flex flex-col gap-[--padding-sm] items-center w-full',
        'bg-[var(--match-display-bg,#eae9fd)] rounded-[var(--corner-xs,4px)]',
        'px-[--padding-xs] py-[--padding-sm]',
        className
      )}
    >
      {/* ── Team info row ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-center gap-[--padding-xs] w-full shrink-0">
        {/* Left team: name → logo */}
        <div className="flex min-w-0 flex-1 items-center justify-end gap-[8px]">
          <Typography variant="paragraph010" className="truncate font-bold">
            {leftTeam.name}
          </Typography>
          <div className="shrink-0">{leftTeam.logo}</div>
        </div>

        {/* Centre badge */}
        {hasScore ? (
          /* Score + status pill */
          <div className="shrink-0 flex flex-col items-stretch">
            <div className="bg-[var(--match-score-bg,#ffffff)] rounded-t-[4px] bg-white px-[8px] py-[2px] flex items-center justify-center">
              <Typography
                as="span"
                variant="subheadline020"
                className="text-[var(--match-score-text,#222526)] whitespace-nowrap"
              >
                {leftTeam.score} - {rightTeam.score}
              </Typography>
            </div>
            {status && (
              <div
                className={twMerge(
                  'rounded-b-[4px] bg-[#201D4A] px-[8px] py-[2px]',
                  'flex items-center justify-center',
                  statusVariant === 'live'
                    ? 'bg-[var(--match-status-live-bg,#8ce28b)]'
                    : 'bg-[var(--match-status-ft-bg,#201d4a)]'
                )}
              >
                <Typography
                  as="span"
                  variant="paragraph000"
                  className={twMerge(
                    'whitespace-nowrap font-bold  text-[10px]',
                    statusVariant === 'live'
                      ? 'text-[var(--match-status-live-text,#222526)]'
                      : 'text-[var(--match-status-ft-text,#ffffff)]'
                  )}
                >
                  {status}
                </Typography>
              </div>
            )}
          </div>
        ) : kickOff ? (
          /* Kick-off time pill */
          <div className="shrink-0 bg-[var(--match-kickoff-bg,#ffffff)] px-[--padding-2xs] py-[--padding-3xs] rounded-[var(--corner-xs,4px)] flex items-center justify-center">
            <Typography
              as="span"
              variant="subheadline010"
              className="text-[var(--match-kickoff-text,#222526)] whitespace-nowrap"
            >
              {kickOff}
            </Typography>
          </div>
        ) : null}

        {/* Right team: logo → name */}
        <div className="flex min-w-0 flex-1 items-center justify-start gap-[8px]">
          <div className="shrink-0">{rightTeam.logo}</div>
          <Typography variant="paragraph010" className="truncate font-bold">
            {rightTeam.name}
          </Typography>
        </div>
      </div>

      {/* ── Venue / date line ─────────────────────────────────────────────── */}
      {venue && (
        <Typography
          as="p"
          variant="paragraph000"
          className="w-full text-center text-[var(--match-display-text,#222526)] overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {venue}
        </Typography>
      )}
    </div>
  );
};
